import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, StateToken } from '@ngxs/store';
import { tap } from 'rxjs/operators';
import { getTenantId } from 'src/app/services/store-snapshot.service';
import { hasWorkingCopyId } from '../functions/has-working-copy-id';
import {
  DocumentWorkingCopyIntent,
  IDocumentWorkingCopy,
} from '../models/document-working-copy.model';
import { DocumentWorkingCopyApiService } from '../services/document-working-copy-api.service';
import {
  ClearDocumentWorkingCopy,
  DeleteDocumentWorkingCopy,
  LoadDocumentWorkingCopy,
  SaveDocumentWorkingCopy,
  SetDocumentWorkingCopyIntent,
  UpdateDocumentWorkingCopy,
} from './document-working-copy.actions';

export interface DocumentWorkingCopyStateModel {
  workingCopy?: IDocumentWorkingCopy;
  intent: DocumentWorkingCopyIntent;
}

const DOCUMENT_WORKING_COPY_STATE_TOKEN = new StateToken<DocumentWorkingCopyStateModel>(
  'documentWorkingCopy'
);

@State<DocumentWorkingCopyStateModel>({
  name: DOCUMENT_WORKING_COPY_STATE_TOKEN,
  defaults: {
    workingCopy: undefined,
    intent: 'unspecified',
  },
})
@Injectable()
export class DocumentWorkingCopyState {
  constructor(private service: DocumentWorkingCopyApiService) {}

  @Action(LoadDocumentWorkingCopy, { cancelUncompleted: true })
  loadDocumentWorkingCopy({ getState, setState }: StateContext<DocumentWorkingCopyStateModel>) {
    return this.service.getWorkingCopy().pipe(
      tap((workingCopy: IDocumentWorkingCopy | undefined) => {
        setState({
          ...getState(),
          workingCopy: hasWorkingCopyId(workingCopy)
            ? this.withSessionTenant(workingCopy)
            : undefined,
        });
      })
    );
  }

  @Action(SaveDocumentWorkingCopy)
  saveDocumentWorkingCopy(
    { getState, setState }: StateContext<DocumentWorkingCopyStateModel>,
    { workingCopy, tenantId }: SaveDocumentWorkingCopy
  ) {
    const current = getState().workingCopy;
    const sessionTenantId = tenantId || this.sessionTenantId(current);
    return this.service.saveWorkingCopy(workingCopy, sessionTenantId).pipe(
      tap((response: IDocumentWorkingCopy | null) => {
        if (this.hasTenantMovedOn(sessionTenantId)) {
          return;
        }

        setState({
          ...getState(),
          workingCopy: this.mergeSavedWorkingCopy(
            getState().workingCopy,
            workingCopy,
            response,
            sessionTenantId
          ),
        });
      })
    );
  }

  @Action(UpdateDocumentWorkingCopy)
  updateDocumentWorkingCopy(
    { getState, setState }: StateContext<DocumentWorkingCopyStateModel>,
    { workingCopy, tenantId }: UpdateDocumentWorkingCopy
  ) {
    const current = getState().workingCopy;
    const sessionTenantId = tenantId || this.sessionTenantId(current);
    return this.service.updateWorkingCopy(workingCopy, sessionTenantId).pipe(
      tap((response: IDocumentWorkingCopy | null) => {
        if (this.hasTenantMovedOn(sessionTenantId)) {
          return;
        }

        setState({
          ...getState(),
          workingCopy: this.withSessionTenant(
            hasWorkingCopyId(response)
              ? response
              : {
                  ...current,
                  documentId: workingCopy.documentId,
                  content: workingCopy.content,
                  sourceNewestVersionId: workingCopy.sourceNewestVersionId,
                  lastAutosavedAt: new Date().toISOString(),
                },
            current,
            sessionTenantId
          ),
        });
      })
    );
  }

  @Action(DeleteDocumentWorkingCopy)
  deleteDocumentWorkingCopy({ getState, setState }: StateContext<DocumentWorkingCopyStateModel>) {
    return this.service.deleteWorkingCopy(this.sessionTenantId(getState().workingCopy)).pipe(
      tap(() => {
        setState({
          ...getState(),
          workingCopy: undefined,
        });
      })
    );
  }

  @Action(SetDocumentWorkingCopyIntent)
  setDocumentWorkingCopyIntent(
    { getState, setState }: StateContext<DocumentWorkingCopyStateModel>,
    { intent }: SetDocumentWorkingCopyIntent
  ) {
    setState({
      ...getState(),
      intent,
    });
  }

  @Action(ClearDocumentWorkingCopy)
  clearDocumentWorkingCopy({ setState }: StateContext<DocumentWorkingCopyStateModel>) {
    setState({
      workingCopy: undefined,
      intent: 'unspecified',
    });
  }

  @Selector()
  static getWorkingCopy(state: DocumentWorkingCopyStateModel) {
    return state.workingCopy;
  }

  @Selector()
  static getIntent(state: DocumentWorkingCopyStateModel) {
    return state.intent;
  }

  private mergeSavedWorkingCopy(
    current: IDocumentWorkingCopy | undefined,
    request: SaveDocumentWorkingCopy['workingCopy'],
    response: IDocumentWorkingCopy | null,
    tenantId?: string
  ): IDocumentWorkingCopy {
    return this.withSessionTenant(
      {
        ...current,
        documentId: request.documentId,
        content: request.content,
        sourceNewestVersionId: request.sourceNewestVersionId,
        lastAutosavedAt: new Date().toISOString(),
        ...(response ?? {}),
      },
      current,
      tenantId
    );
  }

  private withSessionTenant(
    workingCopy: IDocumentWorkingCopy,
    previous?: IDocumentWorkingCopy,
    tenantId?: string
  ): IDocumentWorkingCopy {
    return {
      ...workingCopy,
      tenantId:
        tenantId ||
        this.sessionTenantId(previous) ||
        this.sessionTenantId(workingCopy) ||
        getTenantId(),
    };
  }

  private sessionTenantId(workingCopy?: IDocumentWorkingCopy): string | undefined {
    return workingCopy?.tenantId || undefined;
  }

  private hasTenantMovedOn(sessionTenantId?: string): boolean {
    return !!sessionTenantId && sessionTenantId !== getTenantId();
  }
}
